import { useNavigate } from 'react-router-dom';
import { useInfiniteQuery } from '@tanstack/react-query';
import { Inbox } from 'lucide-react';
import { api, apiError } from '@/lib/api';
import { dateTime, money } from '@/lib/format';
import { useI18n } from '@/i18n';
import { useAuth } from '@/context/AuthContext';
import { MethodIcon, Notice, PageLoading, PageTitle, Spinner, StatusPill } from '@/components/ui';
import { NeedAccount } from './NeedAccount';
import type { Tx } from '@/types/api';

const PAGE = 20;

export function HistoryPage() {
    const { t, te, lang } = useI18n();
    const { me } = useAuth();
    const navigate = useNavigate();
    const q = useInfiniteQuery({
        queryKey: ['transactions', 'history'],
        queryFn: async ({ pageParam }) => (await api.get<{ total: number; items: Tx[] }>('/transactions', { params: { limit: PAGE, skip: pageParam } })).data,
        initialPageParam: 0,
        getNextPageParam: (last, pages) => {
            const loaded = pages.reduce((n, p) => n + p.items.length, 0);
            return loaded < last.total ? loaded : undefined;
        },
        enabled: !!me?.registered,
        staleTime: 10_000
    });
    if (!me?.registered) return <NeedAccount />;
    const items = q.data?.pages.flatMap(p => p.items) || [];
    return (
        <div>
            <PageTitle title={t('history.title')} subtitle={t('history.subtitle')} />
            {q.isLoading && <PageLoading />}
            {q.isError && <Notice tone="warn">{te(apiError(q.error))}</Notice>}
            {q.data && !items.length && (
                <div className="card text-center py-10 space-y-2">
                    <Inbox className="w-8 h-8 mx-auto text-ink-muted" />
                    <p className="text-sm text-ink-muted">{t('history.empty')}</p>
                </div>
            )}
            <div className="space-y-2">
                {items.map(tx => (
                    <button key={tx.operationId} className="card card-tap w-full flex items-center gap-3 text-left py-3" onClick={() => navigate(`/tx/${tx.operationId}`)}>
                        <MethodIcon kind={tx.kind} size="sm" />
                        <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold truncate">{t(`tx.type.${tx.type}`)} · {tx.method}</div>
                            <div className="text-[11px] text-ink-muted">{dateTime(tx.createdAt, lang)}</div>
                        </div>
                        <div className="text-right space-y-1">
                            <div className={`text-sm font-bold ${tx.type === 'deposit' ? 'text-brand-200' : ''}`}>{tx.type === 'deposit' ? '+' : '−'}{money(tx.amount, tx.currency, lang)}</div>
                            <StatusPill status={tx.status} />
                        </div>
                    </button>
                ))}
            </div>
            {q.hasNextPage && (
                <button className="btn-secondary w-full mt-3" disabled={q.isFetchingNextPage} onClick={() => q.fetchNextPage()}>
                    {q.isFetchingNextPage ? <Spinner className="w-4 h-4" /> : null} {t('history.more')}
                </button>
            )}
        </div>
    );
}
